<template>
  <c-loading-area :loading="loading">
    <v-card-title>
      {{ $t('page.manageFiling.origCourt.stepCase.title') }}
    </v-card-title>
    <v-card-subtitle>
      {{ $t('page.manageFiling.origCourt.stepCase.description') }}
    </v-card-subtitle>
    <v-card-text class="pt-2">
      <v-row no-gutters>
        <v-col cols="12">
          <c-autocomplete
            v-model="bean.originatingCourt"
            :items="courts"
            :label="$t('page.manageFiling.origCourt.stepCase.field.originatingCourt')"
            item-value="courtID"
            item-text="courtName"
            key="originatingCourt"
            return-object
            required
            :clearable="false"
            outlined
            hide-details="auto"
            aria-describedby="f260e6bc-96d3-4693-9f6c-1334729ec509"/>
          <field-description id="f260e6bc-96d3-4693-9f6c-1334729ec509"
                             message-key="page.manageFiling.origCourt.stepCase.field.originatingCourt.description"/>
        </v-col>
        <v-col cols="12">
          <c-text-field
            v-model="bean.caseNumber"
            :label="$t('page.manageFiling.origCourt.stepCase.field.caseNumber')"
            maxlength="50"
            key="caseNumber"
            required
            outlined
            hide-details="auto"
            aria-describedby="aa3fc0a9-68ea-44fb-9da3-0ea3aca69f33"/>
          <field-description id="aa3fc0a9-68ea-44fb-9da3-0ea3aca69f33"
                             message-key="page.manageFiling.origCourt.stepCase.field.caseNumber.description"/>
        </v-col>
        <v-col cols="12">
          <c-text-field
            v-model="bean.caseTitle"
            :label="$t('page.manageFiling.origCourt.stepCase.field.caseTitle')"
            maxlength="500"
            key="caseTitle"
            outlined
            hide-details="auto"
            aria-describedby="1827bdc6-b8d6-40ef-aff5-423df6be0c37"/>
          <field-description id="1827bdc6-b8d6-40ef-aff5-423df6be0c37"
                             message-key="page.manageFiling.origCourt.stepCase.field.caseTitle.description"/>
        </v-col>
        <v-col cols="12">
          <c-text-field
            v-model="bean.caseType"
            :label="$t('page.manageFiling.origCourt.stepCase.field.caseType')"
            maxlength="250"
            key="caseType"
            outlined
            hide-details="auto"
            aria-describedby="a3d4c1af-6743-4875-8085-11fc407e2c99"/>
          <field-description id="a3d4c1af-6743-4875-8085-11fc407e2c99"
                             message-key="page.manageFiling.origCourt.stepCase.field.caseType.description"/>
        </v-col>
        <v-col cols="12">
          <c-autocomplete
            v-model="bean.county"
            :items="counties"
            :label="$t('page.manageFiling.origCourt.stepCase.field.county')"
            item-value="countyID"
            item-text="countyName"
            return-object
            outlined
            hide-details="auto"
            aria-describedby="0ba0413e-4943-4754-8187-6f4fd3823092"/>
          <field-description id="0ba0413e-4943-4754-8187-6f4fd3823092"
                             message-key="page.manageFiling.origCourt.stepCase.field.county.description"/>
        </v-col>
        <transition-group name="scroll-y-transition" tag="div" style="width:100%">
          <v-col cols="12" v-for="(rcn, index) in bean.relatedCaseNumbers"
                 :key="`col-relatedCaseNumber-${index}`" :class="`d-flex pa-0 mb-${relatedCaseNumberBottomMargin(index)}`">
            <template>
              <c-text-field
                v-model="bean.relatedCaseNumbers[index]"
                :label="$t('page.manageFiling.origCourt.stepCase.field.relatedCaseNumber')"
                maxlength="50"
                :id="`relatedCaseNumber-${index}`"
                :key="`relatedCaseNumber-${index}`"
                outlined
                hide-details="auto"
                aria-describedby="3e0dac33-7ca0-450c-87aa-43a899ab6a1d"/>
              <v-btn v-if="shouldDisplayRemove" icon style="flex:0 0 auto" class="ml-4 mt-3"
                     @click="removeRelatedCaseNumber($event, `${index}`)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
          </v-col>
        </transition-group>
        <field-description id="3e0dac33-7ca0-450c-87aa-43a899ab6a1d"
                           message-key="page.manageFiling.origCourt.stepCase.field.relatedCaseNumber.description"/>

        <v-col cols="12" class="mt-0">
          <button type="button" class="link-button" @click="addRelatedCaseNumber">
            {{ $t('page.manageFiling.origCourt.stepCase.button.addRelatedCaseNumber') }}
          </button>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="justify-end mt-5">
      <c-btn type="primary" @click="save">
        {{ editing ? $t('global.save') : $t('global.next') }}
      </c-btn>
      <c-btn @click="$emit('cancel')">
        {{ $t('global.cancel') }}
      </c-btn>
    </v-card-actions>
  </c-loading-area>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import WorkflowStepMixin from '@/mixins/workflowStep';
import { focusNext } from '@/application/utility';

export default {
  mixins: [ WorkflowStepMixin ],
  props: {
    editing: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      courts: [],
      counties: []
    };
  },
  computed: {
    shouldDisplayRemove () {
      return this.bean.relatedCaseNumbers.length > 1;
    }
  },
  created () {
    if (_.isEmpty(this.bean.relatedCaseNumbers))
    {
      this.bean.relatedCaseNumbers.push('');
    }
  },
  methods: {
    loadData () {
      let courtURL = this.$cove.getAPI({ name: 'api.cms.courts', query: { size: this.CONST.PAGING_MAX_SIZE } });
      let courtsPromise = axios.get(courtURL)
        .then((response) => {
          this.courts = _.orderBy(_.get(response, 'data', []), 'courtName');
        });

      let countiesURL = this.$cove.getAPI({ name: 'api.cms.counties', query: { size: this.CONST.PAGING_MAX_SIZE } });
      let countiesPromise = axios.get(countiesURL)
        .then((response) => {
          this.counties = _.orderBy(_.get(response, 'data', []), 'countyName');
        });

      return Promise.all([courtsPromise, countiesPromise]);

    },
    validate () {
      this.$cove.validation.clear();

      this.$cove.validation.required(this.bean.originatingCourt.courtID,
        'page.manageFiling.origCourt.stepCase.field.originatingCourt', 'originatingCourt');
      this.$cove.validation.required(this.bean.caseNumber,
        'page.manageFiling.origCourt.stepCase.field.caseNumber', 'caseNumber');

      return !this.$cove.validation.hasErrors();
    },
    save () {
      let isValid = this.validate();
      if (!isValid)
      {
        this.scrollTo(this.CONST.ERROR_CONTAINER_SELECTOR);
      }
      else
      {
        this.bean.relatedCaseNumbers = _.compact(this.bean.relatedCaseNumbers);
        this.$emit('save', this.bean);
      }
    },
    addRelatedCaseNumber () {
      this.bean.relatedCaseNumbers.push('');
    },
    removeRelatedCaseNumber (e, index) {
      focusNext(e);
      this.bean.relatedCaseNumbers.splice(index, 1);
    },
    relatedCaseNumberBottomMargin (index) {
      return index < this.bean.relatedCaseNumbers.length - 1 ? '6':'0';
    }
  }
};
</script>